// apps/client/src/config/reportRegistry.ts
//
// Canonical single source of truth for the grantable reports: the 12
// broker-grantable reports, plus the 3 admin-tier valuation reports appended
// for per-user `office_admin` grants (see ADMIN_REPORT_SLUGS at the foot).
//
// Before this file, the 12-report slug list was duplicated across four sites
// that INTENTIONALLY diverge:
//   - pages/team/TeamManagementPage.tsx           REPORT_OPTIONS         (12 slugs, incl. book-trend)
//   - hooks/useAccessRestrictions.ts              BROKER_REPORT_SLUGS    (12 slugs)
//   - routes/guards/reportAccessUtils.ts          REPORT_SLUG_TO_PATH    (11 slugs, NO book-trend)
//   - components/layout/MorphingSidebar.tsx       inline slugToPath      (11 slugs, NO book-trend)
//   - components/layout/UnifiedTopBar.tsx         pathToReportSlug gate  (10 of these; NO book-trend/commission-settings)
//
// Those divergences are DELIBERATE (they encode real per-surface behaviour) and
// must NOT be "cleaned up". This registry records each divergence explicitly via
// the per-site flags below so `resolveReportAccess.ts` can reproduce every site
// verbatim while the definitions live in exactly one place.
//
// PR A1 introduces this file but wires nothing to it. PR A2 swaps the nav/guard
// consumers to the resolver built on top of it; the committed golden fixture is
// the byte-for-byte no-regression proof.

import type { Feature } from './subscriptionTiers';

export type ReportSlug =
  | 'settlement-age'
  | 'clawbacks'
  | 'arrears'
  | 'discharged-loans'
  | 'loans'
  | 'trail-drop-off'
  | 'book-health'
  | 'customer-portfolio'
  | 'refinance-intelligence'
  | 'acquired-books'
  | 'commission-settings'
  | 'book-trend'
  // Admin-tier valuation reports. NOT broker-grantable (the top bar hides all
  // three from the broker family); they exist here so an `office_admin` can be
  // granted them per-user via `allowedReports`, which is the only role whose
  // grant list is honoured for them.
  | 'trail-book-valuation'
  | 'trail-forecast'
  | 'rcti-cashflow';

export interface ReportDefinition {
  slug: ReportSlug;
  /** Human label as shown in the Team page grant list (REPORT_OPTIONS). */
  label: string;
  /** Canonical route. `commission-settings` is the only non-`/reports/*` path. */
  path: string;
  /**
   * Broker-family users (broker / broker_assistant / credit_analyst) can be
   * granted this report per-user via `userData.allowedReports`. All 12 current
   * reports are broker-grantable; the flag exists so future non-grantable
   * reports can be added without a per-user control.
   */
  brokerGrantable: boolean;
  /**
   * Grantable per-user to an `office_admin` via `allowedReports`. The three
   * valuation reports are admin-tier only (the broker family never sees them),
   * so they are `adminGrantable` but NOT `brokerGrantable`; the 12 original
   * reports are the reverse. Nothing is both today. Consumed by the Manage
   * Access modal so a grant list can only ever name slugs the target's role can
   * actually be granted - the server validates the same union
   * (`BROKER_REPORT_SLUGS` / `ADMIN_REPORT_SLUGS`) and would otherwise reject
   * the save with `invalid-argument`.
   */
  adminGrantable?: boolean;
  /** Tier feature that unlocks this report. Undefined = available on every tier. */
  requiredFeature?: Feature;
  /**
   * Per-tenant tri-state flag on `companyData.featureFlags` ('off'|'observe'|
   * 'enabled'); the report unlocks only when the value is exactly 'enabled'.
   */
  triStateFlag?: string;
  /**
   * Per-tenant boolean flag on `companyData.featureFlags` that must be `=== true`
   * for the report to unlock (independent of tier). Currently only
   * `commission-settings` (flag `commission_settings`).
   */
  requiredFlag?: string;
  /**
   * When true, the report also unlocks for tenants that own acquired books,
   * even if `requiredFeature` is not satisfied. Mirrors UnifiedTopBar's
   * `hasAcquiredBooks || grantedFeatures.refinance_intelligence` gate for
   * Acquired Books.
   */
  unlockedByAcquiredBooks?: boolean;

  // ---- per-site divergence flags (verbatim record of today's differences) ----

  /**
   * Present in routes/guards/reportAccessUtils.ts REPORT_SLUG_TO_PATH (11 slugs).
   * Only `book-trend` is absent (a broker whose first grant is book-trend falls
   * back to the default report path). Drives `firstAllowedReportPath`.
   */
  inGuardPathMap: boolean;
  /**
   * Present in components/layout/UnifiedTopBar.tsx `pathToReportSlug` gate map.
   * Absent for `book-trend` and `commission-settings` (both handled by bespoke
   * logic, not the granular broker filter). Drives the top-bar report gate.
   */
  inTopBarGating: boolean;
  /**
   * Present in components/layout/MorphingSidebar.tsx inline `slugToPath` for the
   * Reports-link target (11 slugs; `book-trend` absent). Drives
   * `sidebarReportsLinkTarget`.
   */
  sidebarFirstReportEligible: boolean;
}

/**
 * The 12 reports in canonical (Team page REPORT_OPTIONS) order. Order is
 * load-bearing for the committed golden fixture - do not reorder without
 * regenerating and reviewing the golden diff.
 */
export const REPORT_REGISTRY: readonly ReportDefinition[] = [
  {
    slug: 'settlement-age',
    label: 'Settlement Age',
    path: '/reports/settlement-age',
    brokerGrantable: true,
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'clawbacks',
    label: 'Clawbacks',
    path: '/reports/clawbacks',
    brokerGrantable: true,
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'arrears',
    label: 'Arrears',
    path: '/reports/arrears',
    brokerGrantable: true,
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'discharged-loans',
    label: 'Discharged Loans',
    path: '/reports/discharged-loans',
    brokerGrantable: true,
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'loans',
    label: 'Loan Portfolio',
    path: '/reports/loans',
    brokerGrantable: true,
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'trail-drop-off',
    label: 'Loan Trail Drop-Off',
    path: '/reports/trail-drop-off',
    brokerGrantable: true,
    requiredFeature: 'trail_drop_off',
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'book-health',
    label: 'Book Health',
    path: '/reports/book-health',
    brokerGrantable: true,
    requiredFeature: 'book_health',
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'customer-portfolio',
    label: 'Customer Portfolio',
    path: '/reports/customer-portfolio',
    brokerGrantable: true,
    requiredFeature: 'customer_portfolio',
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'refinance-intelligence',
    label: 'Client Retention',
    path: '/reports/refinance-intelligence',
    brokerGrantable: true,
    requiredFeature: 'refinance_intelligence',
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'acquired-books',
    label: 'Acquired Books',
    path: '/reports/acquired-books',
    brokerGrantable: true,
    requiredFeature: 'refinance_intelligence',
    unlockedByAcquiredBooks: true,
    inGuardPathMap: true,
    inTopBarGating: true,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'commission-settings',
    label: 'Commission Settings',
    path: '/my-commission-settings',
    brokerGrantable: true,
    requiredFlag: 'commission_settings',
    inGuardPathMap: true,
    // NOT in the top-bar granular gate: commission settings is surfaced by a
    // dedicated sidebar entry, not the Reports mega-menu filter.
    inTopBarGating: false,
    sidebarFirstReportEligible: true,
  },
  {
    slug: 'book-trend',
    label: 'Book Trend Report',
    path: '/reports/book-trend',
    brokerGrantable: true,
    triStateFlag: 'broker_book_trend_report',
    // Absent from every path/gate map: book-trend has bespoke tri-state + role +
    // allowedReports gating in the top bar and is never a Reports-link target.
    inGuardPathMap: false,
    inTopBarGating: false,
    sidebarFirstReportEligible: false,
  },

  // ---- admin-tier valuation reports (appended; see ADMIN_REPORT_SLUGS) ------
  //
  // `inGuardPathMap` and `sidebarFirstReportEligible` are FALSE on all three, so
  // GUARD_SLUG_TO_PATH and SIDEBAR_SLUG_TO_PATH in resolveReportAccess.ts, both
  // derived by filtering on those flags, are byte-identical to before this
  // change. `inTopBarGating` is TRUE (see below), so TOPBAR_PATH_TO_SLUG does
  // gain three entries; that map is consulted only by the granular per-user
  // filter, which runs for a constrained caller alone, and the three paths it
  // adds are never in a constrained caller's item list. Inert, but by a
  // different argument from the other two maps.
  //
  // They are appended rather than inserted because the committed golden fixture
  // is position-indexed: appending leaves rows 0-11 meaning exactly what they
  // did.
  //
  // `inTopBarGating: true` is what makes an office_admin's grant list actually
  // govern these three nav entries - without it she would be SHOWN a report the
  // server then denies her (the F6 failure mode). It is inert for the broker
  // family: all three sit behind `isAdminReportRole` (ADMIN_REPORT_ROUTE_ROLES)
  // in UnifiedTopBar, so they are never in a broker's item list to begin with and
  // filtering them removes nothing that was there.
  {
    slug: 'trail-book-valuation',
    label: 'Trail Book Valuation',
    path: '/reports/trail-book-valuation',
    brokerGrantable: false,
    adminGrantable: true,
    requiredFeature: 'trail_book_valuation',
    inGuardPathMap: false,
    inTopBarGating: true,
    sidebarFirstReportEligible: false,
  },
  {
    slug: 'trail-forecast',
    label: 'Trail Forecast',
    path: '/reports/trail-forecast',
    brokerGrantable: false,
    adminGrantable: true,
    requiredFeature: 'trail_forecast',
    inGuardPathMap: false,
    inTopBarGating: true,
    sidebarFirstReportEligible: false,
  },
  {
    slug: 'rcti-cashflow',
    label: 'RCTI Cashflow',
    path: '/reports/rcti-cashflow',
    brokerGrantable: false,
    adminGrantable: true,
    requiredFeature: 'rcti_cashflow',
    inGuardPathMap: false,
    inTopBarGating: true,
    sidebarFirstReportEligible: false,
  },
] as const;

/**
 * The three admin-tier valuation slugs, mirroring the server's
 * `ADMIN_REPORT_SLUGS` (packages/shared-utils/src/auth/resolveAccessRestrictions.ts).
 * Both sides must know the same three names; a drift here is a save that the
 * server rejects with `invalid-argument`.
 */
export const ADMIN_REPORT_SLUGS: readonly ReportSlug[] = [
  'trail-book-valuation',
  'trail-forecast',
  'rcti-cashflow',
] as const;

/**
 * Roles admitted by the RequireRole guard around the admin-only report routes in
 * AppRoutes.tsx (trail-run-off, trail-book-valuation, trail-forecast,
 * rcti-cashflow, succession-readiness, referral-partners). Every nav surface that
 * offers one of those routes (UnifiedTopBar, topBarPortfolioItems, the command
 * palette) must gate on this same list: an item the route refuses redirects the
 * user to "/", and a Reports sub-section click opens its FIRST item.
 * Pinned to AppRoutes by UnifiedTopBarAdminReportRoles.test.tsx.
 */
export const ADMIN_REPORT_ROUTE_ROLES: readonly string[] = ['super_admin', 'administrator', 'office_admin'];

/**
 * Reports a given target role can be granted per-user.
 *
 * Broker-family users keep exactly the 12 original reports, so this is
 * byte-identical to the pre-change catalogue for them. An `office_admin` is
 * granted from the UNION (12 broker reports + the 3 valuation reports), which
 * is precisely what the server validates an office_admin's `allowedReports`
 * against (`BROKER_REPORT_SLUGS` ∪ `ADMIN_REPORT_SLUGS`). Every other role
 * carries no per-user grant list at all, so it sees the full catalogue.
 */
export function grantableReportsForRole(role: string | null | undefined): readonly ReportDefinition[] {
  // office_admin is the ONLY role whose grant list the server validates against
  // the union, so it is the only role offered the three valuation reports.
  if (role === 'office_admin') {
    return REPORT_REGISTRY.filter((d) => d.brokerGrantable || d.adminGrantable);
  }
  // Everyone else - the broker family AND the admin tier - gets exactly the 12
  // broker-grantable reports, which is the whole registry as it stood before
  // the valuation entries were appended. Returning REPORT_REGISTRY here instead
  // would hand an administrator or super_admin target the three admin slugs,
  // and `updateBrokerAccessPermissions` validates a NON-office_admin target
  // against BROKER_REPORT_SLUGS alone, so the save would come back
  // `invalid-argument` after the admin had ticked a switch the UI offered.
  return REPORT_REGISTRY.filter((d) => d.brokerGrantable);
}

/** Fallback report path used by the sidebar and route guards. */
export const DEFAULT_REPORT_PATH = '/reports/settlement-age';

/**
 * Where a route guard sends a user whose grant list contains NO report it can
 * map to a route.
 *
 * It must be a path that is NOT itself wrapped in `RequireReportAccess`, or the
 * redirect is a self-redirect and the browser spins. `DEFAULT_REPORT_PATH`
 * cannot serve: `/reports/settlement-age` IS guarded (AppRoutes.tsx:422), so a
 * user without the `settlement-age` grant bounces off it straight back into the
 * same redirect. `/reports/trail-run-off` cannot serve either, despite being
 * unguarded, because its `RequireRole` admits only super_admin, administrator
 * and office_admin (AppRoutes.tsx:537) - a broker sent there is bounced again.
 * The dashboard is the one destination every role can actually reach.
 */
export const NO_REACHABLE_REPORT_PATH = '/';

/** Sidebar Reports-link fallback when a broker has grants but none resolve. */
export const SIDEBAR_DEFAULT_REPORT_PATH = '/reports/trail-drop-off';

/** Reports-link target for non-broker, non-marketing roles. */
export const SIDEBAR_NON_BROKER_REPORT_PATH = '/reports/trail-run-off';

/**
 * Per-tenant report suppression (FAIL-OPEN).
 *
 * Some reports can never populate for a tenant whose aggregator does not supply
 * the underlying data: Finsure sends no arrears feed at all, and no
 * lender-approved loan amount (which Approved vs Settled needs to compute a
 * variance). An empty page is a bad first impression, so those reports are
 * hideable per tenant with a single Firestore write:
 * `companies/{companyId}.featureFlags.<flag> = false`.
 *
 * Direction matters. `requiredFlag` / `triStateFlag` above are fail-CLOSED (the
 * report stays hidden until the flag is set); suppression is fail-OPEN: ONLY an
 * explicit `false` hides the report. Absent, `true`, or any other value leaves
 * today's behaviour untouched, so no existing tenant is affected. This mirrors
 * the server-side entitlement checker, which likewise denies on
 * `featureFlags[feature] === false` before any tier grant. Deleting the flag
 * restores the report: the page and its route are never removed.
 */
export const TENANT_SUPPRESSIBLE_REPORT_FLAGS = [
  'arrears',
  'approved_vs_settled',
  // Trail Leakage: the nav entry was pushed unconditionally for super_admin /
  // administrator so the feature stayed discoverable, but a tenant holding
  // NEITHER side of its entitlement union (trail_leakage OR broker_activity)
  // only ever reaches the upgrade prompt. Suppression is still fail-open, so
  // discovery is preserved everywhere the flag is absent.
  'trail_leakage',
] as const;

export type TenantSuppressibleReportFlag = (typeof TENANT_SUPPRESSIBLE_REPORT_FLAGS)[number];

/**
 * Whether the tenant has explicitly switched a suppressible report off.
 * Pure; safe to call with a missing/partial `featureFlags` map.
 */
export function isReportSuppressedForTenant(
  featureFlags: Record<string, unknown> | null | undefined,
  flag: TenantSuppressibleReportFlag,
): boolean {
  return featureFlags?.[flag] === false;
}
